import { useState, useEffect } from 'react'
import { Card, Tabs, Form, Input, Button, Switch, Select, InputNumber, message, Divider, Space, Popconfirm } from 'antd'
import { SaveOutlined, ReloadOutlined } from '@ant-design/icons'

const { Option } = Select
const { TextArea } = Input



export default function Settings() {
  const [loading, setLoading] = useState(false)
  const [basicForm] = Form.useForm()
  const [orderForm] = Form.useForm()
  const [smsForm] = Form.useForm()
  const [paymentForm] = Form.useForm()

  useEffect(() => {
    // Fetch real config from API
    const fetchConfig = async () => {
      try {
        const res = await fetch('/api/system-configs')
        const json = await res.json()
        if (json.code === 200 && json.data) {
          // Transform API data to form fields
          const config: any = {
            site_name: json.data.site_name || '智厨云',
            site_logo: json.data.site_logo || '/logo.jpg',
            contact_phone: json.data.contact_phone || '400-888-8888',
            order_auto_cancel_minutes: parseInt(json.data.auto_cancel_minutes || '30'),
            order_auto_complete_days: parseInt(json.data.auto_complete_days || '7'),
            delivery_fee_free_amount: parseFloat(json.data.free_delivery_amount || '58'),
            delivery_fee: parseFloat(json.data.delivery_fee || '5'),
            enable_sms: json.data.sms_enabled === 'true',
            points_per_yuan: parseInt(json.data.points_rate || '100'),
          }
          basicForm.setFieldsValue(config)
          orderForm.setFieldsValue(config)
          smsForm.setFieldsValue(config)
        }
      } catch (err) {
        console.error('Failed to load config:', err)
      }
    }
    fetchConfig()
  }, [])

  const handleSaveBasic = async () => {
    try {
      await basicForm.validateFields()
      setLoading(true)
      const values = basicForm.getFieldsValue()
      await fetch('/api/system-configs/batch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_name: values.site_name,
          site_logo: values.site_logo,
          contact_phone: values.contact_phone,
        })
      })
      message.success('基础设置保存成功')
    } catch (err) {
      message.error('保存失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveOrder = async () => {
    try {
      await orderForm.validateFields()
      setLoading(true)
      const values = orderForm.getFieldsValue()
      await fetch('/api/system-configs/batch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          auto_cancel_minutes: String(values.order_auto_cancel_minutes),
          auto_complete_days: String(values.order_auto_complete_days),
          free_delivery_amount: String(values.delivery_fee_free_amount),
          delivery_fee: String(values.delivery_fee),
        })
      })
      message.success('订单设置保存成功')
    } catch (err) {
      message.error('保存失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveSMS = async () => {
    try {
      await smsForm.validateFields()
      setLoading(true)
      const values = smsForm.getFieldsValue()
      await fetch('/api/system-configs/batch', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sms_enabled: String(values.enable_sms),
        })
      })
      message.success('短信设置保存成功')
    } catch (err) {
      message.error('保存失败')
    } finally {
      setLoading(false)
    }
  }

  const handleSavePayment = async () => {
    try {
      await paymentForm.validateFields()
      setLoading(true)
      await new Promise(resolve => setTimeout(resolve, 500))
      message.success('支付设置保存成功')
    } catch (err) {
      message.error('保存失败')
    } finally {
      setLoading(false)
    }
  }

  const handleClearCache = () => {
    message.success('缓存清除成功')
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Tabs
          items={[
            {
              key: 'basic',
              label: '基础设置',
              children: (
                <Form form={basicForm} labelCol={{ span: 4 }} wrapperCol={{ span: 16 }}>
                  <Form.Item name="site_name" label="网站名称" rules={[{ required: true }]}>
                    <Input />
                  </Form.Item>
                  <Form.Item name="site_logo" label="网站Logo">
                    <Input />
                  </Form.Item>
                  <Form.Item name="contact_phone" label="联系电话">
                    <Input />
                  </Form.Item>
                  <Form.Item name="contact_address" label="联系地址">
                    <Input />
                  </Form.Item>
                  <Form.Item wrapperCol={{ offset: 4, span: 16 }}>
                    <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSaveBasic}>
                      保存设置
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'order',
              label: '订单设置',
              children: (
                <Form form={orderForm} labelCol={{ span: 6 }} wrapperCol={{ span: 12 }}>
                  <Form.Item name="order_auto_cancel_minutes" label="未支付自动取消(分钟)">
                    <InputNumber min={5} max={120} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="order_auto_complete_days" label="自动完成天数">
                    <InputNumber min={1} max={30} style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="delivery_fee_free_amount" label="免运费金额">
                    <InputNumber min={0} prefix="¥" style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="delivery_fee" label="默认运费">
                    <InputNumber min={0} prefix="¥" style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item wrapperCol={{ offset: 6, span: 12 }}>
                    <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSaveOrder}>
                      保存设置
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'sms',
              label: '短信设置',
              children: (
                <Form form={smsForm} labelCol={{ span: 6 }} wrapperCol={{ span: 12 }}>
                  <Form.Item name="enable_sms" label="启用短信" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="sms_provider" label="短信服务商">
                    <Select>
                      <Option value="aliyun">阿里云</Option>
                      <Option value="tencent">腾讯云</Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="sms_access_key" label="AccessKey">
                    <Input.Password />
                  </Form.Item>
                  <Form.Item name="sms_secret_key" label="SecretKey">
                    <Input.Password />
                  </Form.Item>
                  <Form.Item name="sms_sign_name" label="短信签名">
                    <Input />
                  </Form.Item>
                  <Form.Item name="sms_template_code" label="验证码模板ID">
                    <Input />
                  </Form.Item>
                  <Form.Item wrapperCol={{ offset: 6, span: 12 }}>
                    <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSaveSMS}>
                      保存设置
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'payment',
              label: '支付设置',
              children: (
                <Form form={paymentForm} labelCol={{ span: 6 }} wrapperCol={{ span: 12 }}>
                  <Divider orientation="left">微信支付</Divider>
                  <Form.Item name="enable_wechat_pay" label="启用微信支付" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="wechat_app_id" label="AppID">
                    <Input />
                  </Form.Item>
                  <Form.Item name="wechat_mch_id" label="商户号">
                    <Input />
                  </Form.Item>
                  <Form.Item name="wechat_api_key" label="API密钥">
                    <Input.Password />
                  </Form.Item>

                  <Divider orientation="left">支付宝</Divider>
                  <Form.Item name="enable_alipay" label="启用支付宝" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="alipay_app_id" label="AppID">
                    <Input />
                  </Form.Item>
                  <Form.Item name="alipay_private_key" label="应用私钥">
                    <TextArea rows={3} />
                  </Form.Item>

                  <Form.Item wrapperCol={{ offset: 6, span: 12 }}>
                    <Button type="primary" icon={<SaveOutlined />} loading={loading} onClick={handleSavePayment}>
                      保存设置
                    </Button>
                  </Form.Item>
                </Form>
              )
            },
            {
              key: 'system',
              label: '系统维护',
              children: (
                <Card>
                  <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                      <h4>清除缓存</h4>
                      <p style={{ color: '#999' }}>清除系统缓存数据，解决部分显示异常问题</p>
                      <Popconfirm title="确认清除缓存?" onConfirm={handleClearCache}>
                        <Button icon={<ReloadOutlined />}>清除缓存</Button>
                      </Popconfirm>
                    </div>
                    <Divider />
                    <div>
                      <h4>系统信息</h4>
                      <p>版本：v1.0.0</p>
                      <p>技术栈：Node.js + React + MySQL</p>
                      <p>部署时间：2026-04-06</p>
                    </div>
                  </Space>
                </Card>
              )
            }
          ]}
        />
      </Card>
    </div>
  )
}
